import { useMemo, useState } from 'react';
import { AbsenceDialog } from './AbsenceDialog';
import { OvertimeDialog } from './OvertimeDialog';
import { EmployeeAvailability } from './EmployeeAvailability';
import { AvailabilityMatrix, type DaySelection } from './AvailabilityMatrix';
import { EmptyState } from '../../components/ui/Field';
import { useAbsencesInRange, useBalances, useEmployees, useHolidays } from '../../lib/queries';
import { addDays, formatRange, todayIso, weekRange, type DateRange } from '../../lib/dates';
import type { Absence, Employee, SessionUser } from '../../lib/types';
import './verfuegbarkeiten.css';

/** Wie viele Wochen die Übersicht auf einmal zeigt. */
const WEEKS = 4;

function defaultRange(): DateRange {
  const week = weekRange(todayIso());
  return { from: week.from, to: addDays(week.from, WEEKS * 7 - 1) };
}

/**
 * Verfügbarkeit als Matrix: Namen links, Tage oben.
 *
 * Grün heißt verfügbar, Rot heißt nicht verfügbar. Rot entsteht ausschließlich
 * aus eingetragenen Abwesenheiten — dieselbe Quelle, aus der auch der
 * Schichtplan seine Sperren zieht. Ein zweiter Vorrat „verfügbar/nicht
 * verfügbar" würde unweigerlich vom Urlaubskonto abweichen, und dann wüsste
 * niemand mehr, welcher der beiden stimmt.
 */
export function VerfuegbarkeitenView({ user }: { user: SessionUser }) {
  const canEdit = user.role !== 'betrachter';

  const [range, setRange] = useState<DateRange>(defaultRange);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ absence: Absence | null; prefill?: DaySelection } | null>(
    null,
  );
  const [addingOvertime, setAddingOvertime] = useState(false);
  const [detail, setDetail] = useState<Employee | null>(null);

  const employees = useEmployees();
  const absences = useAbsencesInRange(range);
  const holidays = useHolidays(Number(range.from.slice(0, 4)));
  const balances = useBalances(Number(range.from.slice(0, 4)));

  const activeEmployees = useMemo(
    () => (employees.data ?? []).filter((employee) => employee.active),
    [employees.data],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return activeEmployees
      .filter((employee) => (term ? employee.displayName.toLowerCase().includes(term) : true))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'));
  }, [activeEmployees, search]);

  const balanceByEmployee = useMemo(
    () => new Map((balances.data?.balances ?? []).map((entry) => [entry.employeeId, entry])),
    [balances.data],
  );

  const shift = (direction: -1 | 1) =>
    setRange((current) => ({
      from: addDays(current.from, direction * WEEKS * 7),
      to: addDays(current.to, direction * WEEKS * 7),
    }));

  if (employees.isLoading) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Verfügbarkeiten werden geladen …
      </div>
    );
  }

  return (
    <div className="view-full">
      <header className="plan-toolbar no-print">
        <div className="plan-stepper">
          <button type="button" className="btn btn-icon" onClick={() => shift(-1)} aria-label="Zurück">
            ‹
          </button>
          <button type="button" className="btn" onClick={() => setRange(defaultRange())}>
            Heute
          </button>
          <button type="button" className="btn btn-icon" onClick={() => shift(1)} aria-label="Weiter">
            ›
          </button>
        </div>

        <span className="plan-range-label">{formatRange(range, 'frei')}</span>

        <input
          type="search"
          className="input"
          placeholder="Name suchen …"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Mitarbeiter suchen"
        />

        <div className="spacer" />

        {canEdit ? (
          <>
            <button
              type="button"
              className="btn btn-signal"
              onClick={() => setEditing({ absence: null })}
            >
              Abwesenheit eintragen
            </button>
            <button type="button" className="btn" onClick={() => setAddingOvertime(true)}>
              Überstunden buchen
            </button>
          </>
        ) : null}
      </header>

      {visible.length === 0 ? (
        <div className="card">
          <EmptyState
            title="Keine Mitarbeiter gefunden"
            description="Im Reiter „Mitarbeiter“ anlegen, oder die Suche zurücksetzen."
          />
        </div>
      ) : (
        <AvailabilityMatrix
          employees={visible}
          absences={absences.data ?? []}
          holidays={holidays.data ?? []}
          range={range}
          canEdit={canEdit}
          onNameClick={setDetail}
          onSelect={(selection) => setEditing({ absence: selection.absence, prefill: selection })}
        />
      )}

      <footer className="avail-legend no-print">
        <span className="avail-legend-item">
          <span className="avail-swatch is-free" /> verfügbar
        </span>
        <span className="avail-legend-item">
          <span className="avail-swatch is-blocked" /> nicht verfügbar
        </span>
        <span className="avail-legend-item">
          <span className="avail-swatch is-pending" /> beantragt, noch offen
        </span>
        <div className="spacer" />
        {canEdit ? (
          <span>Feld anklicken oder über mehrere Tage ziehen · Name anklicken zeigt das Konto</span>
        ) : null}
      </footer>

      {editing ? (
        <AbsenceDialog
          absence={editing.absence}
          employees={activeEmployees}
          defaultEmployeeId={editing.prefill?.employee.id}
          defaultRange={
            editing.prefill ? { from: editing.prefill.from, to: editing.prefill.to } : undefined
          }
          onClose={() => setEditing(null)}
        />
      ) : null}

      {addingOvertime ? (
        <OvertimeDialog employees={activeEmployees} onClose={() => setAddingOvertime(false)} />
      ) : null}

      {detail ? (
        <EmployeeAvailability
          employee={detail}
          balance={balanceByEmployee.get(detail.id)}
          employees={activeEmployees}
          canEdit={canEdit}
          onClose={() => setDetail(null)}
        />
      ) : null}
    </div>
  );
}
